import { useCallback, useEffect, useRef, useState } from "react";
import { Search, UserX } from "lucide-react";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/format";
import { useT } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CoachCard } from "@/components/marketing/CoachCard";
import { EmptyState } from "@/components/EmptyState";

export default function Coaches() {
  const t = useT();
  usePageMeta(t("cpg.meta"), t("cpg.meta_desc"));

  const [q, setQ] = useState("");
  const [coaches, setCoaches] = useState(null);
  const debounceRef = useRef(null);

  const fetchCoaches = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/public/coaches", { params });
    setCoaches(data || []);
  }, [q]);

  useEffect(() => {
    setCoaches(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCoaches().catch(() => setCoaches([]));
    }, 280);
    return () => clearTimeout(debounceRef.current);
  }, [fetchCoaches]);

  return (
    <div>
      <div className="border-b border-border bg-secondary/40">
        <div className="container-page py-12">
          <h1 className="font-display text-4xl text-foreground">{t("cpg.title")}</h1>
          <p className="mt-2 max-w-xl text-muted-foreground">
            {t("cpg.subtitle")}
          </p>
          <div className="relative mt-7 max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("cpg.search_ph")}
              className="h-11 rounded-full bg-card pl-10"
              data-testid="coaches-search"
            />
          </div>
        </div>
      </div>

      <div className="container-page py-10">
        {coaches === null ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}
          </div>
        ) : coaches.length === 0 ? (
          <EmptyState
            icon={UserX}
            title={t("cpg.empty_title")}
            description={t("cpg.empty_desc")}
            action={q ? <Button variant="outline" className="rounded-full" onClick={() => setQ("")}>{t("cpg.reset_search")}</Button> : null}
          />
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {coaches.map((c) => <CoachCard key={c.id} coach={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}
